import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { Programme } from "../../features/training/models/Programme";
import { updateProgramme } from "../../features/training/slice-extensions/training-slice-extension";
import { formatDateOnly } from "../../features/training/utils/formatDateOnly";
import ProgrammeTopicsTable from "./ProgrammeTopicsTable";

interface ProgrammeDetailsPanelProps {
  programme: Programme;
  canManage: boolean;
}

/** One programme: its name, template stamp, archive switch (Owner/Admin) and its topics. */
const ProgrammeDetailsPanel: React.FC<ProgrammeDetailsPanelProps> = ({ programme, canManage }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [name, setName] = useState(programme.name);

  useEffect(() => setName(programme.name), [programme.name]);

  const save = (nextName: string, isArchived: boolean) =>
    dispatch(updateProgramme({ id: programme.id, request: { name: nextName, isArchived } }));
  const nameDirty = name.trim() !== "" && name.trim() !== programme.name;

  return (
    // min-w-0 lets the grid column shrink to the phone width, so the topics table scrolls instead of clipping.
    <div className="with-border min-w-0 rounded-2xl p-4 lg:p-6 space-y-4">
      {canManage ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            aria-label={t("training.programmes.name")}
            className="input w-full sm:max-w-md"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
          />
          <button type="button" className="btn btn-primary" disabled={!nameDirty}
            onClick={() => save(name.trim(), programme.isArchived)}>
            {t("training.programmes.saveName")}
          </button>
        </div>
      ) : (
        <h2 className="text-lg font-semibold">{programme.name}</h2>
      )}
      <div className="flex flex-wrap items-center gap-4 text-sm text-grey-50 dark:text-grey-70">
        {programme.licenceClass && (
          <span>{t("training.programmes.licenceClass")}: {programme.licenceClass}</span>
        )}
        {programme.templateVersion && (
          <span>
            {t("training.programmes.templateVersion", {
              date: formatDateOnly(programme.templateVersion, i18n.language),
            })}
          </span>
        )}
        {canManage ? (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={programme.isArchived}
              onChange={(e) => save(programme.name, e.target.checked)} />
            {t("training.programmes.archived")}
          </label>
        ) : (
          programme.isArchived && <span>{t("training.programmes.archived")}</span>
        )}
      </div>
      <ProgrammeTopicsTable programme={programme} canManage={canManage} />
    </div>
  );
};

export default ProgrammeDetailsPanel;
